import { Center, Modal, SegmentedControl } from '@mantine/core';
import {
    IconBox,
    IconCheck,
    IconExclamationCircle,
    IconEye,
    IconKey,
    IconLock,
    IconSettings,
    IconTerminal,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InstanceResponseModel } from '../../../apis/v1/instances/model';
import { Avatar, Button, InputField } from '../../Atoms';
import Editor from '../../Atoms/Editor/Editor';
import WrappedText from '../../Atoms/WrappedText/WrappedText';

/** What the modal shows; build one per product (`toConnectTarget` for cloud instances). */
export interface ConnectTarget {
    hostname: string;
    ipAddress: string;
    username: string;
    /** Undefined when the API withheld it (view-only access); the modal then says so. */
    password?: string;
    /** Switches the guide to RDP on port 3389 instead of SSH on port 22. */
    isWindows: boolean;
}

export function toConnectTarget(instance: InstanceResponseModel): ConnectTarget {
    const isWindows = instance.image.os.toLowerCase().includes('windows');
    return {
        hostname: instance.hostname,
        ipAddress: instance.network.ipAddress,
        username: isWindows ? 'Administrator' : 'xver',
        password: instance.password,
        isWindows,
    };
}

interface ConnectInstanceModalProps {
    /** Pass null while no row is selected; the modal renders nothing. */
    target: ConnectTarget | null;
    opened: boolean;
    onClose: () => void;
    /** Omit when the product has no console; the console button is then hidden. */
    onOpenConsole?: () => void;
}

/**
 * Connection instructions for one server: SSH commands, an ~/.ssh/config snippet and
 * credentials, or the RDP variant when `target.isWindows`.
 */
export default function ConnectInstanceModal({
    target,
    opened,
    onClose,
    onOpenConsole,
}: ConnectInstanceModalProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const [guide, setGuide] = useState('terminal');

    if (!target) return null;

    const { isWindows } = target;
    // The Windows variant has no ssh-config tab; without this the body renders blank
    // when the tab is carried over from a Linux instance.
    const activeGuide = isWindows && guide === 'ssh-config' ? 'terminal' : guide;

    const openConsole = (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        onOpenConsole?.();
    };

    return (
        <Modal
            size={isWindows ? 'lg' : 'md'}
            opened={opened}
            onClose={onClose}
            title={
                <div className="w-full flex flex-row justify-start items-center gap-2.5">
                    <Avatar value={target.hostname} size={'44'} />
                    <div className="flex flex-col justify-center items-start gap-0.5">
                        <h3 className="mt-1.5 text-[16px] font-bold">
                            {t(isWindows ? 'connect.windowsTitle' : 'connect.linuxTitle', {
                                hostname: target.hostname,
                            })}
                        </h3>
                        <div className="inline-flex items-center gap-1">
                            <span className="text-[14px] text-fg/78">{target.ipAddress}</span>
                            <span className="text-[30px] text-fg/50">·</span>
                            <span className="text-[14px] text-fg/68">
                                {t('connect.port', { port: isWindows ? 3389 : 22 })}
                            </span>
                        </div>
                    </div>
                </div>
            }
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
            }}
            styles={{
                body: {
                    padding: 0,
                },
            }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="w-full flex flex-col justify-start item-start">
                <div className="w-full p-3 flex flex-col justify-start items-start gap-1.5 border-t-[0.5px] border-b-[0.5px] border-line">
                    <SegmentedControl
                        aria-label={t('connect.guideLabel')}
                        size="sm"
                        radius={'md'}
                        styles={{
                            root: {
                                width: '100%',
                                backgroundColor: 'var(--surface-2)',
                                border: '0.5px solid var(--line)',
                            },
                        }}
                        value={activeGuide}
                        onChange={setGuide}
                        withItemsBorders={false}
                        data={[
                            {
                                value: 'terminal',
                                label: (
                                    <Center style={{ gap: 6 }}>
                                        <IconTerminal size={16} />
                                        <span style={{ fontWeight: 400 }}>
                                            {t(
                                                isWindows ? 'connect.rdpClient' : 'connect.terminal'
                                            )}
                                        </span>
                                    </Center>
                                ),
                            },
                            ...(isWindows
                                ? []
                                : [
                                      {
                                          value: 'ssh-config',
                                          label: (
                                              <Center style={{ gap: 6 }}>
                                                  <IconSettings size={16} />
                                                  <span style={{ fontWeight: 400 }}>
                                                      {t('connect.sshConfig')}
                                                  </span>
                                              </Center>
                                          ),
                                      },
                                  ]),
                            {
                                value: 'credentials',
                                label: (
                                    <Center style={{ gap: 6 }}>
                                        <IconLock size={16} />
                                        <span style={{ fontWeight: 400 }}>
                                            {t('connect.credentials')}
                                        </span>
                                    </Center>
                                ),
                            },
                        ]}
                    />

                    {!isWindows && activeGuide === 'terminal' && (
                        <>
                            <Editor
                                size={'sm'}
                                textSize={'md'}
                                readOnly
                                height={'42px'}
                                filename={t('connect.quickConnect')}
                                defaultValue={`ssh ${target.username}@${target.ipAddress}`}
                                hideLineCharsLabel
                                hideScrollbar
                                disableWrap
                                enableCopy
                            />
                            <Editor
                                size={'sm'}
                                textSize={'md'}
                                readOnly
                                height={'42px'}
                                filename={t('connect.withExplicitPort')}
                                defaultValue={`ssh -p 22 ${target.username}@${target.ipAddress}`}
                                hideLineCharsLabel
                                hideScrollbar
                                disableWrap
                                enableCopy
                            />
                        </>
                    )}

                    {!isWindows && activeGuide === 'ssh-config' && (
                        <>
                            <div className="text-[14px] text-fg/68">
                                {t('connect.pastePrefix')}
                                <WrappedText className="mx-1 text-fg">~/.ssh/config</WrappedText>
                                {t('connect.pasteSuffix')}
                                <WrappedText className="text-fg">ssh {target.hostname}</WrappedText>
                                .
                            </div>
                            <Editor
                                size={'sm'}
                                textSize={'md'}
                                readOnly
                                height={'148px'}
                                filename={t('connect.sshConfigSnippet')}
                                defaultValue={[
                                    `Host ${target.hostname}`,
                                    `    HostName ${target.ipAddress}`,
                                    `    User ${target.username}`,
                                    '    Port 22',
                                    '    IdentityFile ~/.ssh/rsa',
                                    '    IdentitiesOnly yes',
                                ].join('\n')}
                                hideLineCharsLabel
                                hideScrollbar
                                disableWrap
                                enableCopy
                            />
                        </>
                    )}

                    {isWindows && activeGuide === 'terminal' && (
                        <>
                            <span className="text-[13px] text-fg/68">
                                {t('connect.rdpDescription')}
                            </span>
                            <div className="w-full grid grid-cols-2 justify-self-center items-center gap-1.5">
                                {[
                                    {
                                        name: 'connect.rdpWindowsName',
                                        hint: 'connect.rdpWindowsHint',
                                    },
                                    { name: 'connect.rdpMacosName', hint: 'connect.rdpMacosHint' },
                                    {
                                        name: 'connect.rdpMobileName',
                                        hint: 'connect.rdpMobileHint',
                                    },
                                    { name: 'connect.rdpLinuxName', hint: 'connect.rdpLinuxHint' },
                                ].map((client) => (
                                    <div
                                        key={client.name}
                                        className="w-full p-2 flex flex-row justify-between items-center gap-2 border-[0.5px] rounded-md border-line bg-surface-2"
                                    >
                                        <div className="flex flex-row justify-start items-center gap-2">
                                            <IconBox size={20} />
                                            <div className="flex flex-col justify-start items-start">
                                                <span className="text-[13px] font-bold">
                                                    {t(client.name)}
                                                </span>
                                                <span className="text-xs text-fg/68">
                                                    {t(client.hint)}
                                                </span>
                                            </div>
                                        </div>
                                        <IconCheck size={20} color="#7ce0a4" />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {activeGuide === 'credentials' && (
                        <>
                            <span className="text-[13px] text-fg/68">
                                {t(
                                    isWindows
                                        ? 'connect.windowsCredentialsHint'
                                        : 'connect.linuxCredentialsHint'
                                )}
                            </span>
                            <div className="w-full my-2 flex flex-col justify-start items-start gap-2">
                                <InputField
                                    size="md"
                                    label={t('connect.username')}
                                    value={target.username}
                                    readOnly
                                    showCopy
                                />
                                {target.password === undefined ? (
                                    <p className="text-[13px] text-fg/68">
                                        {t('projects:resource.passwordHidden')}
                                    </p>
                                ) : (
                                    <InputField
                                        readOnly
                                        label={t('connect.password')}
                                        size="md"
                                        type="password"
                                        value={target.password}
                                        placeholder={t('connect.password')}
                                        passwordReveal
                                        showCopy
                                    />
                                )}
                            </div>
                        </>
                    )}

                    {!isWindows && (
                        <div className="w-full p-2.5 flex flex-row justify-start items-center gap-3 border-[0.5px] border-line rounded-md bg-surface-2">
                            <IconKey size={24} color="oklch(70.8% 0 0)" />
                            <div className="flex flex-col justify-start items-start">
                                <span className="text-[13px] font-bold">
                                    {t('connect.noSshKeyTitle')}
                                </span>
                                <div className="inline-flex items-center text-[13px] text-fg/65">
                                    {t('connect.noSshKeyMessagePrefix')}&nbsp;
                                    <div className="text-blue-400 light:text-blue-700">
                                        {t('connect.noSshKeyMessageLink')}
                                    </div>
                                    &nbsp;
                                    {t('connect.noSshKeyMessageSuffix')}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="w-full p-2.5 flex flex-row justify-start items-center gap-3 border-[0.5px] border-line rounded-md bg-surface-2">
                        <IconExclamationCircle size={24} color="oklch(70.8% 0 0)" />
                        <div className="flex flex-col justify-start items-start">
                            <span className="text-[13px] font-bold">
                                {t(
                                    isWindows
                                        ? 'connect.restrictRdpTitle'
                                        : 'connect.restrictSshTitle'
                                )}
                            </span>
                            <div className="inline-flex items-center text-[13px] text-fg/65">
                                {t(
                                    isWindows
                                        ? 'connect.restrictRdpMessage'
                                        : 'connect.restrictSshMessage'
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full p-3 flex flex-row justify-between items-center">
                    {onOpenConsole ? (
                        <Button
                            variant="secondary"
                            leftIcon={<IconEye size={18} />}
                            onClick={openConsole}
                        >
                            {t(isWindows ? 'connect.openVncConsole' : 'connect.openWebConsole')}
                        </Button>
                    ) : (
                        <span />
                    )}
                    <Button size="sm" onClick={onClose}>
                        {t('connect.done')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
